/**
 * 格式化 Unix 时间戳为指定格式
 * @param timestamp Unix 时间戳（秒或毫秒）
 * @param format 时间格式，默认 "YYYY/M/D H:mm"
 * @returns 格式化后的时间字符串
 *
 * @example
 * formatTimestamp(1763541473) // "2025/11/19 16:37"
 * formatTimestamp(1763541473, "YYYY/M/D HH:mm:ss") // "2025/11/19 16:37:53"
 */
export function formatTimestamp(timestamp: number | string, format: string = "YYYY/M/D H:mm"): string {
  if (!timestamp) return '-';

  // 转换为数字
  const ts = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;

  // 判断是秒还是毫秒（如果小于 10000000000 则认为是秒）
  const date = new Date(ts < 10000000000 ? ts * 1000 : ts);

  if (isNaN(date.getTime())) return '-';

  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 不补零
  const day = date.getDate(); // 不补零
  const hours = date.getHours(); // 不补零
  const hours24 = hours; // 24小时制
  const minutes = String(date.getMinutes()).padStart(2, '0'); // 分钟补零
  const seconds = String(date.getSeconds()).padStart(2, '0'); // 秒补零

  // 替换格式字符串
  return format
    .replace('YYYY', String(year))
    .replace('M', String(month))
    .replace('D', String(day))
    .replace('H', String(hours)) // 不补零的小时
    .replace('mm', minutes)
    .replace('ss', seconds);
}

/**
 * 格式化为相对时间
 * @param timestamp Unix 时间戳（秒或毫秒）
 * @returns 相对时间字符串，如 "刚刚"、"5分钟前"、"2小时前"
 */
export function formatRelativeTime(timestamp: number | string): string {
  if (!timestamp) return '-';

  const ts = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;
  const date = new Date(ts < 10000000000 ? ts * 1000 : ts);

  if (isNaN(date.getTime())) return '-';

  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const relativeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (seconds < 60) return relativeFormatter.format(0, 'second');
  if (minutes < 60) return relativeFormatter.format(-minutes, 'minute');
  if (hours < 24) return relativeFormatter.format(-hours, 'hour');
  if (days < 7) return relativeFormatter.format(-days, 'day');

  // 超过7天显示完整日期
  return formatTimestamp(timestamp, "YYYY/M/D");
}
